/**
 * Stepper del wizard de Activa — progreso por FASES.
 *
 * Antes mostraba un nodo por paso (11, con candado los no alcanzados). Dos problemas:
 *  · Es lo primero que ve el operador y grita "esto es larguísimo". Con 11 pasos, encima,
 *    la fila no entraba y había que scrollearla en horizontal para ubicarse.
 *  · El candado comunica bloqueo, y no hay nada bloqueado: es simplemente lo que todavía no
 *    hiciste. La cerradura mete ansiedad donde alcanza con un punto apagado.
 *
 * Ahora manda la fase ("Tus datos" → "La estrategia" → "La campaña"), que es como la gente
 * piensa el flujo, con su barra de avance, y el conteo fino va en texto una sola vez («Paso N de
 * M»). El detalle paso a paso queda en los puntos de abajo, que además sirven para volver a lo ya
 * visto.
 *
 * Tampoco repite el nombre del paso: lo dice el encabezado del panel, justo debajo. Antes
 * estaba dos veces, a 40px de distancia.
 */

import { useEffect, useRef } from "react";
import { Check } from "lucide-react";

export interface Paso {
  key: string;
  label: string;
  desc: string;
  /**
   * true = este paso no aplica a esta campaña y no se cuenta ni se dibuja.
   *
   * Los índices siguen siendo ABSOLUTOS (el wizard los referencia por nombre desde `P`): lo que
   * cambia es qué se muestra. Renumerar el array rompería todas las referencias; ocultar y
   * renumerar sólo la VISTA deja "Paso 8 de 10" correcto sin tocar la navegación.
   */
  oculto?: boolean;
}

export interface Fase {
  key: string;
  label: string;
  /** índices (0-based) de los pasos que la componen */
  pasos: number[];
}

interface Props {
  steps: Paso[];
  fases: Fase[];
  current: number;
  maxReached: number;
  onGo: (i: number) => void;
}

export default function WizardStepper({ steps, fases, current, maxReached, onGo }: Props) {
  const activoRef = useRef<HTMLButtonElement>(null);
  /** índices ABSOLUTOS de los pasos que sí aplican; la numeración que ve el usuario sale de acá */
  const visibles = steps.map((p, i) => ({ p, i })).filter((x) => !x.p.oculto);
  const posicion = visibles.findIndex((x) => x.i === current); // 0-based dentro de lo visible

  // Con muchos pasos la fila de puntos puede scrollear; si el activo queda fuera de vista,
  // el stepper deja de orientar. `block: "nearest"` evita que salte el scroll de la página.
  useEffect(() => {
    activoRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [current]);

  return (
    <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-4 sm:p-5">
      {/* Las fases, con el avance real de cada una */}
      <div className="grid gap-3 sm:gap-4" style={{ gridTemplateColumns: `repeat(${fases.length}, minmax(0,1fr))` }}>
        {fases.map((f) => {
          // una fase con todos sus pasos ocultos no cuenta como "0 de 0" ni queda a medias
          const pasos = f.pasos.filter((i) => !steps[i]?.oculto);
          const hechos = pasos.filter((i) => i < current).length;
          const activa = pasos.includes(current);
          const completa = pasos.length > 0 && pasos.every((i) => i < current);
          // en la fase activa el paso en curso cuenta como "a medio hacer": el operador está parado ahí
          const avance = completa ? 1 : activa && pasos.length ? (hechos + 0.5) / pasos.length : 0;
          // Tocar la fase lleva a su primer paso ya visto (pedido de Alan del 26/10/2026: «tocando en los steps que me deje
          // volver para atrás»). Los puntos de abajo siguen, pero son chicos y no se veían.
          const destino = pasos.find((i) => i <= maxReached) ?? null;
          const puedeIr = destino !== null && destino !== current;
          const verbo = destino !== null && destino < current ? "Volver a" : "Ir a";
          const Cabeza = puedeIr ? "button" : "div";

          return (
            // en columna, con la barra al pie: si un título va en dos renglones, las barras siguen alineadas
            <div key={f.key} className="min-w-0 flex flex-col">
              <Cabeza
                {...(puedeIr
                  ? { type: "button" as const, onClick: () => onGo(destino as number), title: `${verbo} «${f.label}»`, "aria-label": `${verbo} «${f.label}»` }
                  : {})}
                className={`flex items-center gap-1.5 mb-2 text-left ${puedeIr ? "rounded-md -mx-1 px-1 hover:bg-slate-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400" : ""}`}
              >
                {completa ? (
                  <span className="w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                    <Check size={10} strokeWidth={3} />
                  </span>
                ) : (
                  <span
                    className={`w-4 h-4 rounded-full flex-shrink-0 border-2 transition-colors ${
                      activa ? "border-violet-600 bg-violet-600 ring-4 ring-violet-100" : "border-slate-200"
                    }`}
                  />
                )}
                {/* Las fases que todavía no llegaron estaban en slate-300: 1.48:1 contra el
                    blanco, cuando AA pide 4.5:1. No eran "suaves", eran invisibles — y son
                    justamente las que le anticipan al operador qué le falta. slate-500 da
                    4.77:1 y la jerarquía la sigue marcando el color (violeta = acá estás,
                    verde = listo) más la barra de avance, no el borrarlas. */}
                <span
                  // En dos renglones si no entra, no cortado: con cuatro fases (el oficial, desde el paso «Horario y baja»)
                  // a 770 px se leía «CUÁNDO Y BAJ…» y «PROBAR Y ENVI…» (26/10). Un span y no un <p>: puede ir dentro del botón.
                  className={`block text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.1em] leading-tight break-words transition-colors ${
                    completa ? "text-emerald-600" : activa ? "text-violet-700" : "text-slate-500"
                  }`}
                >
                  {f.label}
                </span>
              </Cabeza>

              <div className="mt-auto h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${completa ? "bg-emerald-500" : "bg-violet-600"}`}
                  style={{ width: `${Math.round(avance * 100)}%` }}
                />
              </div>

              {/* Sin «0 de 2» debajo de cada fase: no decía de qué, y con el «Paso N de M» de abajo el mismo
                  avance se contaba dos veces. La barra ya muestra cuánto falta de la fase. */}
            </div>
          );
        })}
      </div>

      {/* El conteo fino + los puntos para volver. El nombre del paso NO va acá: lo dice el
          encabezado del panel, 40px más abajo. */}
      <div className="flex items-center justify-between gap-3 mt-4 pt-3.5 border-t border-slate-50">
        <p className="text-xs text-slate-500 tabular-nums flex-shrink-0">
          <span className="font-bold text-slate-700">Paso {Math.max(1, posicion + 1)}</span> de {visibles.length}
        </p>

        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-1">
          {visibles.map(({ p, i }, n) => {
            const visitado = i <= maxReached;
            const esActual = i === current;
            const hecho = i < maxReached;
            return (
              <button
                key={p.key}
                type="button"
                ref={esActual ? activoRef : undefined}
                disabled={!visitado}
                onClick={() => visitado && onGo(i)}
                title={visitado ? `Paso ${n + 1} · ${p.label}` : `${p.label} — todavía no`}
                aria-label={`Paso ${n + 1}: ${p.label}`}
                aria-current={esActual ? "step" : undefined}
                className={`h-1.5 rounded-full flex-shrink-0 transition-all focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-1 ${
                  esActual
                    ? "w-6 bg-violet-600"
                    : hecho
                      ? "w-1.5 bg-emerald-400 hover:w-3 hover:bg-emerald-500 cursor-pointer"
                      : visitado
                        ? "w-1.5 bg-slate-300 hover:w-3 hover:bg-violet-400 cursor-pointer"
                        : "w-1.5 bg-slate-100 cursor-default"
                }`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
